import { create, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { Messages } from '@dojo/framework/i18n/i18n';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { I18nProperties, DefaultChildrenWNodeFactory } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';
import { forIn } from 'lodash-es';

interface CfgInject {
	inject: string;
}

interface CfgWidget {
	widget: string;
	params?: any;
	is_instructor_led?: boolean;
	kids?: CfgItem[];
}

type CfgItem = string | CfgWidget | CfgInject;

interface ResourceParserIcache {
	data: CfgItem[];
}

export interface ResourceParserProperties {
	messages: Messages;
	topicid: string;
	formatting?: {
		[key: string]: string;
	};
	/** boolean to determine whether to replace tokens before parsing (useful when nested resources) */
	preformat?: boolean;
}

const isInject = (item: any): item is CfgInject =>
	typeof item !== 'undefined' && typeof item !== 'string' && 'inject' in item;
const isString = (item: any): item is string => typeof item === 'string';
const isWidget = (item: any): item is CfgWidget =>
	typeof item !== 'undefined' && typeof item !== 'string' && 'widget' in item;

const hasKids = (item: CfgItem): boolean => {
	if (isWidget(item)) {
		return item.kids !== undefined && Array.isArray(item.kids) && item.kids.length > 0;
	}
	return false;
};

const icache = createICacheMiddleware<ResourceParserIcache>();
const factory = create({ icache }).properties<ResourceParserProperties>();

export const ResourceParser = factory(({ properties, middleware: { icache } }) => {
	let keyIndex = 0;
	const { topicid, messages, formatting = {}, preformat = false } = properties();

	const resolveFormatting = (str: string): string => {
		forIn(formatting, (value, key) => {
			const regex = new RegExp(`{${key}}`);
			str = str.replace(regex, value);
			console.info(key, ' :', value);
			console.info('Replaced: ', str);
		});
		return str;
	};

	const resolveInject = (tmp: CfgItem): CfgItem | CfgItem[] => {
		if (isString(tmp)) {
			return formatting ? resolveFormatting(tmp) : tmp;
		}

		if (isInject(tmp)) {
			const key = tmp.inject;
			const tmpdata = formatting ? resolveFormatting(messages[key]) : messages[key];
			const impdata: CfgItem[] = JSON.parse(tmpdata);
			return impdata;
		}

		if (hasKids(tmp)) {
			const kids = tmp
				.kids!.map((item) => {
					return resolveInject(item);
				})
				.flat();
			if (kids.length > 0) {
				tmp.kids = kids;
			}
		}

		return tmp;
	};

	const data = icache.getOrSet('data', () => {
		let tmp: CfgItem[] = [];
		const message = topicid in messages ? messages[topicid] : messages.default;
		const jsonstr = preformat ? resolveFormatting(message) : message;
		try {
			tmp = JSON.parse(jsonstr);
		} catch (e) {
			console.warn(`json parse error ${e}`, jsonstr);
		}
		return tmp
			.map((item) => {
				return resolveInject(item);
			})
			.flat();
	});

	// Logic to check if the widget is for instructor led
	const instructorish: boolean = window.hcstatic.instructorish ?? false;
	const filterData = (dataSet: CfgItem[]) => {
		for (let i = dataSet.length - 1; i >= 0; i--) {
			const item = dataSet[i];
			if (isWidget(item)) {
				const is_instructor_led: boolean = item.is_instructor_led ? item.is_instructor_led : false;
				if (hasKids(item)) {
					filterData(item.kids!);
				}
				if (!instructorish && is_instructor_led) {
					dataSet.splice(i, 1);
				}
			}
		}
	};
	filterData(data);

	const walkItem = (item: CfgItem): DNode => {
		if (isString(item)) {
			return item;
		}

		if (isWidget(item)) {
			const widget: string = item.widget;
			const params: { [key: string]: any } = item.params ? item.params : {};
			params.key = `${topicid}-${keyIndex}`;
			keyIndex += 1;
			const kids: DNode[] = hasKids(item) ? item.kids!.map((kid) => walkItem(kid)) : [];

			return w(widget, params, kids);
		}

		return null;
	};

	return data.map((item) => walkItem(item));
});
